/*
 * Size Recommender
 * ----------------
 * A lightweight, rule-based "Find my size" helper for the product page.
 *
 * How it works:
 *  - Reads the product's real Size option values straight from the rendered
 *    variant picker (so it only ever recommends a size the product actually has,
 *    and it skips sizes that are sold out).
 *  - Asks the shopper for height, weight and a fit preference, then maps those
 *    answers proportionally onto the product's own ordered size list. Because the
 *    mapping is proportional, the same logic works across a mixed catalogue
 *    without per-product setup.
 *  - Selects the recommended size in the variant picker for them, reusing the
 *    theme's native radio inputs so price, media, inventory and the URL all
 *    update exactly as if they'd clicked it themselves.
 *
 * It deliberately does no math beyond simple normalisation + thresholds. The
 * thresholds (height/weight ranges, weight influence) are configurable per block
 * in the theme editor.
 */
class SizeRecommender extends HTMLElement {
  constructor() {
    super();
    this.placeTrigger = this.placeTrigger.bind(this);
  }

  connectedCallback() {
    this.header = document.querySelector('header.shopify-section-header');
    this.body = document.querySelector('body');

    this.sectionId = this.dataset.sectionId;
    this.optionPosition = this.dataset.optionPosition;

    this.heightMin = parseFloat(this.dataset.heightMin) || 150; // cm
    this.heightMax = parseFloat(this.dataset.heightMax) || 200; // cm
    this.weightMin = parseFloat(this.dataset.weightMin) || 45; // kg
    this.weightMax = parseFloat(this.dataset.weightMax) || 120; // kg
    // 0–100 : how much weight (vs height) drives the recommendation
    this.weightInfluence = Math.min(
      100,
      Math.max(0, parseFloat(this.dataset.weightInfluence) || 60)
    ) / 100;

    this.trigger = this.querySelector('[data-size-recommender-trigger]');
    this.modal = document.querySelector(this.dataset.modal);
    this.form = this.modal && this.modal.querySelector('[data-size-recommender-form]');
    this.result = this.modal && this.modal.querySelector('[data-size-recommender-result]');
    this.unitInputs = this.modal
      ? this.modal.querySelectorAll('input[name="sr-unit"]')
      : [];

    // No Size option on this product → remove the helper entirely.
    if (!this.getSizeFieldset()) {
      this.style.display = 'none';
      if (this.modal) this.modal.remove();
      return;
    }

    this.placeTrigger();

    this.openBtn = document.querySelector('[data-size-recommender-open]');
    if (this.openBtn) {
      this.openBtn.addEventListener('click', () => this.openModal());
    }

    if (this.modal) {
      const closeBtns = this.modal.querySelectorAll('[data-size-recommender-close]');
      closeBtns.forEach((btn) => btn.addEventListener('click', () => this.closeModal()));
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }
    this.unitInputs.forEach((input) =>
      input.addEventListener('change', () => this.updateUnitLabels())
    );
    this.updateUnitLabels();

    // When the disclaimer opens the size chart, close this modal first. Using the
    // capture phase guarantees we run before the size chart's opener fires, so the
    // page scroll-lock ends up owned by the size chart (not wrongly cleared by us).
    const disclaimerOpener =
      this.modal && this.modal.querySelector('.size-recommender__disclaimer-opener');
    if (disclaimerOpener) {
      disclaimerOpener.addEventListener(
        'click',
        () => {
          this.closeModal();
        },
        true
      );
    }

    // The variant picker can re-render its own markup when the variant changes,
    // which would drop our injected trigger. Re-place it whenever that happens.
    if (typeof window.subscribe === 'function') {
      this.unsubscribe = window.subscribe('variant-change', (payload) => {
        if (payload && payload.data && payload.data.sectionId === this.sectionId) {
          this.placeTrigger();
        }
      });
    }
    // Belt and braces: also watch the picker subtree directly.
    const picker = document.getElementById(`variant-picker-${this.sectionId}`);
    if (picker && 'MutationObserver' in window) {
      this.observer = new MutationObserver(() => this.placeTrigger());
      this.observer.observe(picker, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.observer) this.observer.disconnect();
  }

  openModal() {
    if (!this.modal) return;
    this.modal.removeAttribute('hidden');
    // small delay to allow display:block to apply before triggering transition
    setTimeout(() => {
      this.modal.classList.add('is-open');
    }, 10);

    // change header z-index to be below the modal
    if (this.header) this.header.style.zIndex = '0';
    
    // add overflow-hidden class to body
    if (this.body) this.body.classList.add('overflow-hidden');
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    setTimeout(() => {
      this.modal.setAttribute('hidden', '');
    }, 300); // match CSS transition duration

    // change header z-index to be below the modal
    if (this.header) this.header.style.zIndex = '12';

    // remove overflow-hidden class from body
    if (this.body) this.body.classList.remove('overflow-hidden');
  }

  getSizeFieldset() {
    const picker = document.getElementById(`variant-picker-${this.sectionId}`);
    if (!picker) return null;
    return picker.querySelector(
      `fieldset[data-option-position="${this.optionPosition}"]`
    );
  }

  /* Move the "Find my size" button so it sits beside the Size label. */
  placeTrigger() {
    if (!this.trigger) return;
    const fieldset = this.getSizeFieldset();
    if (!fieldset) return;
    const legend = fieldset.querySelector('legend');
    const anchor = legend || fieldset;
    if (this.trigger.parentElement !== anchor) {
      anchor.appendChild(this.trigger);
    }
    this.trigger.classList.remove('hidden');
  }

  /* The product's real, in-order Size radio inputs. */
  getSizeInputs() {
    const fieldset = this.getSizeFieldset();
    if (!fieldset) return [];
    return Array.from(fieldset.querySelectorAll('input[type="radio"]')).map(
      (el) => ({
        el,
        value: el.value,
        available: !el.classList.contains('disabled') && !el.disabled,
      })
    );
  }

  isImperial() {
    const checked = this.modal.querySelector('input[name="sr-unit"]:checked');
    return checked ? checked.value === 'imperial' : false;
  }

  updateUnitLabels() {
    if (!this.modal) return;
    const imperial = this.isImperial();
    this.modal.querySelectorAll('[data-unit-height]').forEach((el) => {
      el.textContent = imperial ? 'in' : 'cm';
    });
    this.modal.querySelectorAll('[data-unit-weight]').forEach((el) => {
      el.textContent = imperial ? 'lb' : 'kg';
    });
  }

  toMetric(height, weight) {
    if (this.isImperial()) {
      return {
        height: height ? height * 2.54 : null,
        weight: weight ? weight * 0.453592 : null,
      };
    }
    return { height: height || null, weight: weight || null };
  }

  normalise(value, min, max) {
    if (value == null) return null;
    if (max <= min) return 0.5;
    return Math.min(1, Math.max(0, (value - min) / (max - min)));
  }

  /* Walk outward from an index to the nearest in-stock size. */
  nearestAvailable(sizes, index) {
    return index;
    /* Old work that select the nearest available size */
    /**
      if (sizes[index] && sizes[index].available) return index;
      for (let step = 1; step < sizes.length; step++) {
        if (sizes[index - step] && sizes[index - step].available) return index - step;
        if (sizes[index + step] && sizes[index + step].available) return index + step;
      }
      return index; // nothing in stock — return the ideal anyway
    */
  }

  computeRecommendation(rawHeight, rawWeight, fit) {
    const sizes = this.getSizeInputs();
    if (!sizes.length) return null;

    const { height, weight } = this.toMetric(rawHeight, rawWeight);
    const hNorm = this.normalise(height, this.heightMin, this.heightMax);
    const wNorm = this.normalise(weight, this.weightMin, this.weightMax);
    if (hNorm == null && wNorm == null) return null;

    let score;
    if (hNorm != null && wNorm != null) {
      score = wNorm * this.weightInfluence + hNorm * (1 - this.weightInfluence);
    } else {
      score = hNorm != null ? hNorm : wNorm;
    }

    const lastIndex = sizes.length - 1;
    let index = Math.round(score * lastIndex);

    if (fit === 'snug') index -= 1;
    else if (fit === 'relaxed') index += 1;
    index = Math.min(lastIndex, Math.max(0, index));

    const resolvedIndex = this.nearestAvailable(sizes, index);
    return sizes[resolvedIndex];
  }

  applySelection(size) {
    if (!size || !size.el) return;
    size.el.checked = true;
    size.el.dispatchEvent(new Event('change', { bubbles: true }));

    var checked_fit = document.querySelector('input[name="sr-fit"]:checked').value;
    // dispatch an event to notify the variant picker that the size has changed
    document.dispatchEvent(
      new CustomEvent('size:recommended', {
        bubbles: true,
        detail: { size: size.value, fit: checked_fit, available: size.available }
      })
    );

    if (!this.modal) return;
    setTimeout(() => {
      this.closeModal();
    }, 3000);
  }

  /* Returns an error string if height/weight are out of sane range, otherwise null. */
  validateInputs(height, weight) {
    const imperial = this.isImperial();

    // Metric: height 50–300 cm, weight 20–500 kg
    // Imperial: height 20–118 in (~5ft to ~9ft10), weight 44–1100 lb
    const heightMin = imperial ? 20 : 50;
    const heightMax = imperial ? 118 : 300;
    const weightMin = imperial ? 44 : 20;
    const weightMax = imperial ? 1100 : 500;
    const hUnit = imperial ? 'in' : 'cm';
    const wUnit = imperial ? 'lb' : 'kg';

    if (height !== null && (height < heightMin || height > heightMax)) {
      return `Please enter a height between ${heightMin} and ${heightMax} ${hUnit}.`;
    }
    if (weight !== null && (weight < weightMin || weight > weightMax)) {
      return `Please enter a weight between ${weightMin} and ${weightMax} ${wUnit}.`;
    }
    if (height === null && weight === null) {
      return this.dataset.errorText || 'Add your height or weight to get a recommendation.';
    }
    return null;
  }

  onSubmit(event) {
    event.preventDefault();
    const data = new FormData(this.form);
    const height = parseFloat(data.get('sr-height'));
    const fit = data.get('sr-fit') || 'regular';
    const weight = parseFloat(data.get('sr-weight'));

    const parsedHeight = isNaN(height) ? null : height;
    const parsedWeight = isNaN(weight) ? null : weight;

    const validationError = this.validateInputs(parsedHeight, parsedWeight);
    if (validationError) {
      this.showResult(validationError, true);
      return;
    }

    const recommendation = this.computeRecommendation(parsedHeight, parsedWeight, fit);
    const isRecommendationAvailable = recommendation?.available || false;

    if (!recommendation) {
      this.showResult(
        this.dataset.errorText || 'Add your height or weight to get a recommendation.',
        true
      );
      return;
    }
    
    // Making sure that the recommendation is available before applying it.
    if (isRecommendationAvailable) {
      this.applySelection(recommendation);
    }

    const template = isRecommendationAvailable
      ? this.dataset.resultText || 'We recommend size %SIZE% — selected for you.'
      : this.dataset.oosText ||
        'Based on your answers, size %SIZE% is the best fit, but it is currently sold out.';
    this.showResult(template.replace('%SIZE%', recommendation.value), false);
  }

  showResult(message, isError) {
    if (!this.result) return;
    this.result.hidden = false;
    this.result.innerHTML = message;
    this.result.classList.toggle('size-recommender__result--error', !!isError);
  }
}

if (!customElements.get('size-recommender')) {
  customElements.define('size-recommender', SizeRecommender);
}